/* eslint-disable react/jsx-props-no-spreading */
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { format, isAfter, isValid, startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { markInvoicePaidApi } from 'api/invoiceGroup';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';

const MarkInvoicePaidDialog = ({ invoiceId, onClose, onSaved }) => {
  const [paidDate, setPaidDate] = useState(() => startOfDay(new Date()));
  const [pickerError, setPickerError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const isDateValid =
    isValid(paidDate) && !isAfter(startOfDay(paidDate), startOfDay(new Date())) && !pickerError;

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSubmit = async () => {
    if (!isDateValid) {
      enqueueSnackbar('Enter a valid paid date that is not in the future.', { variant: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const response = await markInvoicePaidApi({
        invoiceId,
        paidDate: format(paidDate, 'yyyy-MM-dd'),
        reason: 'Manually Paid',
      });
      enqueueSnackbar(`Invoice #${invoiceId} marked paid`, { variant: 'success' });
      onSaved(response);
    } catch (error) {
      enqueueSnackbar(getGraphQLErrorMessage(error), { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      aria-labelledby='MarkInvoicePaid-header'
      fullWidth
      maxWidth='xs'
    >
      <DialogTitle id='MarkInvoicePaid-header'>Mark Invoice #{invoiceId} as Paid</DialogTitle>
      <DialogContent>
        <DesktopDatePicker
          label='Paid Date'
          inputFormat='MM/dd/yyyy'
          value={paidDate}
          onChange={setPaidDate}
          onError={setPickerError}
          disableFuture
          disabled={isSaving}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              fullWidth
              sx={{ mt: 1 }}
              helperText={
                !isDateValid ? 'Enter a valid date that is not in the future.' : undefined
              }
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          loading={isSaving}
          disabled={!isDateValid}
          onClick={handleSubmit}
        >
          Mark as Paid
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

MarkInvoicePaidDialog.propTypes = {
  invoiceId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default MarkInvoicePaidDialog;
